import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, CreditCard, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSeo } from "@/context/SettingsContext";
import { Btn } from "@/components/common";

const Shell = ({ children, testid }) => (
  <section className="bg-slate-50"><div className="mx-auto max-w-2xl px-4 py-20 sm:py-28"><div className="rounded-3xl border border-slate-200 bg-white p-8 text-center sm:p-12" data-testid={testid}>{children}</div></div></section>
);

export function SaveCardButton({ reference }) {
  const [loading, setLoading] = useState(false);
  const go = async () => {
    setLoading(true);
    try { const r = await api.post(`/public/bookings/${reference}/save-card`, { origin_url: window.location.origin }); window.location.href = r.data.url; } catch (e) { toast.error(errMsg(e)); setLoading(false); }
  };
  return <Btn variant="outline" loading={loading} onClick={go} data-testid="save-card-button"><CreditCard className="h-4 w-4" />Save a card securely with Stripe</Btn>;
}

function usePoll(fn, done) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let n = 0, stop = false;
    const tick = async () => {
      try { const d = await fn(); setData(d); if (done(d) || ++n > 10) return; } catch { setErr(true); return; }
      if (!stop) setTimeout(tick, 2000);
    };
    tick();
    return () => { stop = true; };
  }, []); // eslint-disable-line
  return { data, err };
}

export function CardSaved() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  useSeo("Card Saved");
  const { data, err } = usePoll(() => api.get(`/public/card-setup/${sid}`).then((r) => r.data), (d) => d.status === "completed");
  if (err) return <Shell testid="card-saved-error"><XCircle className="mx-auto h-14 w-14 text-rose-500" /><p className="mt-4 font-display text-2xl font-bold">We couldn't verify your card</p><p className="mt-2 text-slate-600">Your trial request is still received. Our team will arrange payment when they contact you.</p></Shell>;
  if (!data || data.status !== "completed") return <Shell testid="card-saved-pending"><Loader2 className="mx-auto h-10 w-10 animate-spin text-emerald-700" /><p className="mt-4 text-slate-600">Confirming your card with Stripe…</p></Shell>;
  return (
    <Shell testid="card-saved-success">
      <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-600" />
      <p className="mt-6 font-display text-2xl font-bold text-slate-900 sm:text-3xl">Card saved securely</p>
      <p className="mt-3 text-slate-600"><span className="font-semibold capitalize" data-testid="card-saved-details">{data.brand} •••• {data.last4}</span> is on file with Stripe for booking <span className="font-mono font-bold" data-testid="booking-reference">{data.reference}</span>.</p>
      <p className="mx-auto mt-4 max-w-md rounded-xl bg-emerald-50 p-4 text-sm text-emerald-900">You have <b>not</b> been charged. The trial fee is charged only after our team confirms your trial. Thank you! Your trial request has been received and we'll contact you to confirm eligibility, availability, and trial arrangements.</p>
      <Link to="/" className="mt-8 inline-flex h-12 items-center rounded-full bg-slate-900 px-7 font-semibold text-white">Back to home</Link>
    </Shell>
  );
}

export function PaymentResult({ cancelled }) {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  useSeo(cancelled ? "Payment Cancelled" : "Payment");
  const { data, err } = usePoll(() => (cancelled || !sid ? Promise.resolve({ status: "cancelled" }) : api.get(`/public/payment-status/${sid}`).then((r) => r.data)), (d) => d.status !== "pending");
  if (cancelled) return <Shell testid="payment-cancelled"><XCircle className="mx-auto h-14 w-14 text-slate-400" /><p className="mt-4 font-display text-2xl font-bold">Payment cancelled</p><p className="mt-2 text-slate-600">No charge was made. You can use the payment link again whenever you're ready.</p></Shell>;
  if (err) return <Shell testid="payment-error"><p className="font-display text-2xl font-bold">We couldn't check this payment</p></Shell>;
  if (!data || data.status === "pending") return <Shell testid="payment-pending"><Loader2 className="mx-auto h-10 w-10 animate-spin text-emerald-700" /><p className="mt-4 text-slate-600">Confirming payment with Stripe…</p></Shell>;
  const ok = data.status === "succeeded";
  return (
    <Shell testid={ok ? "payment-success" : "payment-failed"}>
      {ok ? <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-600" /> : <XCircle className="mx-auto h-16 w-16 text-rose-500" />}
      <p className="mt-6 font-display text-2xl font-bold">{ok ? "Payment received" : "Payment not completed"}</p>
      <p className="mt-2 text-slate-600">Booking <span className="font-mono font-bold">{data.reference}</span>{ok ? " — thank you. We'll be in touch about delivery." : ". No charge was made."}</p>
    </Shell>
  );
}

export function CardCancelledNotice() {
  const { ref } = useParams();
  const [params] = useSearchParams();
  if (params.get("card") !== "cancelled") return null;
  return (
    <div className="mx-auto mt-8 max-w-lg rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900" data-testid="card-cancelled-notice">
      <p><b>Card details are required to complete your trial request.</b> Your details are saved and you have not been charged, but we can't confirm a trial until a card is on file.</p>
      <div className="mt-3"><SaveCardButton reference={ref} /></div>
    </div>
  );
}
